import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, ButtonProps } from 'primereact/button'

import { useToastContext } from '../../contexts/toast'
import { requestAttachmentDownload } from '../../data/attachments/api'
import { getHandleableError } from '../../utils/errors/isXiaError'

type Props = {
  fullUrl: string
  mimeType: string
  fileName: string
} & ButtonProps

export const AttachmentOpenInTabButton: React.FC<Props> = ({
  fullUrl,
  mimeType,
  fileName,
  ...props
}) => {
  const { t } = useTranslation('attachment')
  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const handleOnClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setIsRequestRunning(true)
    try {
      const attachmentBlob = await requestAttachmentDownload(
        fullUrl,
        mimeType,
        fileName,
      )

      const fileURL = window.URL.createObjectURL(attachmentBlob)
      const fileLink = document.createElement('a')
      fileLink.href = fileURL
      fileLink.target = '_blank'
      document.body.appendChild(fileLink)
      fileLink.click()
      document.body.removeChild(fileLink)
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        detail: errorMessage,
        summary: t('download.messages.error.summary', { name: fileName }),
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const normalizedMimeType = mimeType?.split(';')[0]?.trim()
  const browserSupportedMimeTypes = [
    'application/pdf',
    'application/xml',
    'text/xml',
    'text/plain',
    'text/html',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'video/mp4',
    'audio/mpeg',
  ]
  const isSupportedFileType =
    browserSupportedMimeTypes.includes(normalizedMimeType)

  return (
    <Button
      aria-label={t('openInTab.label')}
      icon={'pi pi-eye'}
      onClick={handleOnClick}
      loading={isRequestRunning}
      rounded
      text
      {...props}
      disabled={props.disabled || !isSupportedFileType}
    />
  )
}
